import { useMemo } from 'react';

import { DefaultError } from '@tanstack/react-query';

import {
  UseNodeDependencies,
  useNodeDependencies,
  UseNodeDependenciesArgs,
} from 'features/cluster/nodes/hooks/useNodeDependencies';
import { NodeWithDependencies } from 'features/cluster/nodes/types';
import { useNodeQuery } from 'hooks/queries/nodes';

import { useCluster } from '@cast/shared-cluster-context-data-access';

type UseNode = {
  node?: NodeWithDependencies;
  isLoading: boolean;
  isWorkloadsLoading: boolean;
  error?: DefaultError;
} & Omit<UseNodeDependencies, 'addNodeDependencies'>;

type UseNodeArgs = {
  nodeId?: string;
  enabled?: boolean;
} & UseNodeDependenciesArgs;

export const useNode = ({
  nodeId,
  clusterId: _clusterId,
  refetchInterval,
  enabled = true,
  ...nodeDependenciesArgs
}: UseNodeArgs = {}): UseNode => {
  const { cluster } = useCluster();
  const clusterId = _clusterId || cluster.id;

  const {
    node: _node,
    isLoading,
    error,
    refetch,
  } = useNodeQuery({
    clusterId,
    nodeId,
    refetchInterval,
    enabled,
  });

  const {
    refetch: refetchDependencies,
    addNodeDependencies,
    hasResultsWithError: hasDependenciesError,
    isWorkloadsLoading,
    ...nodeDependencies
  } = useNodeDependencies({
    clusterId: _clusterId,
    refetchInterval,
    ...nodeDependenciesArgs,
  });

  const node = useMemo(() => {
    if (!_node) {
      return undefined;
    }
    if (!addNodeDependencies) {
      return _node;
    }
    return addNodeDependencies(_node);
  }, [_node, addNodeDependencies]);

  return {
    ...nodeDependencies,
    node,
    isLoading,
    isWorkloadsLoading,
    error: error || undefined,
    hasResultsWithError: Boolean(error || hasDependenciesError),
    refetch: () => {
      refetch();
      refetchDependencies();
    },
  };
};
